import { supabase } from '../config/supabase';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { Event } from '../types/event.types';
import { EventSeries } from '../types/eventSeries.types';
import { locationCache } from '../services/LocationCacheService';
import type { Tables } from '../types/supabase.generated';
import geohash from 'ngeohash';

// Types Supabase
type DbEventRow = Tables<'events'>;
type DbSeriesRow = Tables<'event_series'>;

// Constantes pour le systeme de geohash
const GEOHASH_PRECISION = 5; // ~4.9km x 4.9km - couverture ~15km avec 9 zones

// Type pour les informations de zone geohash
interface GeohashZoneInfo {
  centerHash: string;
  allHashes: string[]; // centre + 8 voisins
  center: { lat: number; lng: number };
  radius: number;
}

// ✅ OPTIMISATION ANTI-RERENDER - Cache pour normalizeSupabaseEvent
class EventNormalizationCache {
  private static cache = new Map<string, { event: Event; timestamp: number }>();
  private static dateCache = new Map<number, Date>();
  private static arrayCache = new Map<string, any[]>();
  private static readonly MAX_SIZE = 200;
  private static readonly TTL = 5 * 60 * 1000; // 5min
  private static cleanupTimer: number | null = null;

  static initializeCleanup(): void {
    if (!this.cleanupTimer) {
      this.cleanupTimer = window.setInterval(() => this.cleanup(), 2 * 60 * 1000);
    }
  }

  static stopCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  static generateSignature(id: string, updatedAt?: string): string {
    return `${id}_${updatedAt || Date.now()}`;
  }

  static getStableDate(timestamp: number): Date {
    let date = this.dateCache.get(timestamp);
    if (!date) {
      date = new Date(timestamp);
      this.dateCache.set(timestamp, date);
      if (this.dateCache.size > this.MAX_SIZE) {
        const oldest = Array.from(this.dateCache.keys()).slice(0, 50);
        oldest.forEach(key => this.dateCache.delete(key));
      }
    }
    return date;
  }

  static getStableArray<T>(array: T[]): T[] {
    if (!Array.isArray(array)) return array;
    const hash = JSON.stringify(array);
    let cached = this.arrayCache.get(hash);
    if (!cached) {
      cached = Object.freeze([...array]) as T[];
      this.arrayCache.set(hash, cached);
      if (this.arrayCache.size > 50) {
        const oldest = Array.from(this.arrayCache.keys()).slice(0, 10);
        oldest.forEach(key => this.arrayCache.delete(key));
      }
    }
    return cached as T[];
  }

  static getCached(signature: string): Event | null {
    const entry = this.cache.get(signature);
    if (entry && Date.now() - entry.timestamp < this.TTL) {
      return entry.event;
    }
    if (entry) this.cache.delete(signature);
    return null;
  }

  static setCached(signature: string, event: Event): void {
    if (this.cache.size >= this.MAX_SIZE) {
      const oldest = Array.from(this.cache.keys()).slice(0, 50);
      oldest.forEach(key => this.cache.delete(key));
    }
    this.cache.set(signature, { event, timestamp: Date.now() });
  }

  static cleanup(): void {
    const now = Date.now();
    const toDelete = Array.from(this.cache.entries())
      .filter(([_, entry]) => now - entry.timestamp > this.TTL)
      .map(([key]) => key);
    toDelete.forEach(key => this.cache.delete(key));
  }

  static getStats() {
    return {
      cacheSize: this.cache.size,
      datesCacheSize: this.dateCache.size,
      arraysCacheSize: this.arrayCache.size
    };
  }

  static clearAll(): void {
    this.cache.clear();
    this.dateCache.clear();
    this.arrayCache.clear();
  }
}

// Interface pour les prix (stockés en JSONB)
interface PriceItem {
  amount: number;
  description: string;
  currency: string;
}

/**
 * Normalise un row Supabase en Event
 */
export const normalizeSupabaseEvent = (row: DbEventRow): Event => {
  // Cast pour accéder aux champs camelCase (événements déjà normalisés depuis IndexedDB)
  const r = row as any;

  const signature = EventNormalizationCache.generateSignature(row.id, row.updated_at ?? r.updatedAt ?? undefined);
  const cached = EventNormalizationCache.getCached(signature);
  if (cached) return cached;

  const toDate = (value: string | Date | null | undefined): Date | undefined => {
    if (!value) return undefined;
    // Si c'est déjà une Date, la retourner directement
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? undefined : EventNormalizationCache.getStableDate(value.getTime());
    }
    const timestamp = new Date(value).getTime();
    return isNaN(timestamp) ? undefined : EventNormalizationCache.getStableDate(timestamp);
  };

  // Support snake_case (Supabase brut) ET camelCase (IndexedDB/déjà normalisé)
  // Note: Les données organizer (organizer, organizerImage, etc.) viennent du cache IndexedDB
  // car elles ne sont plus stockées dans la table events
  const event: Event = {
    id: row.id,
    organizerId: row.organizer_id ?? r.organizerId,
    organizer: r.organizer ?? undefined,
    organizerImage: r.organizerImage ?? undefined,
    organizerSubtitle: r.organizerSubtitle ?? undefined,
    organizerContact: r.organizerContact ?? undefined,
    title: row.title ?? r.title,
    description: row.description ?? r.description ?? undefined,
    datetime: toDate(row.datetime ?? r.datetime),
    endTime: row.end_time ?? r.endTime ?? undefined,
    timezone: (row as any).timezone ?? r.timezone ?? 'Asia/Colombo',
    location: row.location ?? r.location ?? undefined,
    locationName: row.location_name ?? r.locationName ?? undefined,
    latitude: row.latitude ?? r.latitude ?? undefined,
    longitude: row.longitude ?? r.longitude ?? undefined,
    geohash: row.geohash ?? r.geohash ?? undefined,
    tags: EventNormalizationCache.getStableArray(row.tags ?? r.tags ?? []),
    image: row.image ?? r.image ?? undefined,
    prices: EventNormalizationCache.getStableArray((row.prices ?? r.prices ?? []) as unknown as PriceItem[]),
    ticketingUrl: row.ticketing_url ?? r.ticketingUrl ?? undefined,
    views: row.views ?? r.views ?? 0,
    status: (row.status ?? r.status ?? 'draft') as 'active' | 'draft' | 'cancelled',
    createdAt: toDate(row.created_at ?? r.createdAt),
    updatedAt: toDate(row.updated_at ?? r.updatedAt),
    seriesId: row.series_id ?? r.seriesId ?? undefined,
    isException: row.is_exception ?? r.isException ?? false,
    weekDays: EventNormalizationCache.getStableArray(row.week_days ?? r.weekDays ?? []),
    seriesFrequency: (row.series_frequency ?? r.seriesFrequency ?? undefined) as 'daily' | 'weekly' | undefined,
    seriesEndType: (row.series_end_type ?? r.seriesEndType ?? undefined) as 'date' | 'occurrences' | undefined,
    seriesEndDate: toDate(row.series_end_date ?? r.seriesEndDate),
    seriesOccurrences: row.series_occurrences ?? r.seriesOccurrences ?? undefined,
    importConfidence: (row as any).import_confidence ?? r.importConfidence ?? undefined,
    importSource: (row as any).import_source ?? r.importSource ?? undefined,
  };

  const normalized = Object.freeze(event) as Event;
  EventNormalizationCache.setCached(signature, normalized);
  return normalized;
};

/**
 * Normalise un row Supabase en EventSeries
 */
export const normalizeSupabaseSeries = (row: DbSeriesRow): EventSeries => {
  const toDate = (value: string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    const timestamp = new Date(value).getTime();
    return isNaN(timestamp) ? undefined : EventNormalizationCache.getStableDate(timestamp);
  };

  return {
    id: row.id,
    organizerId: row.organizer_id,
    timezone: (row as any).timezone ?? 'Asia/Colombo',
    title: row.title,
    description: row.description ?? undefined,
    location: row.location ?? undefined,
    locationName: row.location_name ?? undefined,
    latitude: row.latitude ?? undefined,
    longitude: row.longitude ?? undefined,
    geohash: row.geohash ?? undefined,
    image: row.image ?? undefined,
    tags: row.tags ?? [],
    prices: (row.prices as unknown as PriceItem[]) ?? [],
    ticketingUrl: row.ticketing_url ?? undefined,
    frequency: row.frequency as 'daily' | 'weekly',
    weekDays: row.week_days ?? [],
    startTime: row.start_time ?? undefined,
    endTime: row.end_time ?? undefined,
    startDate: row.start_date ?? undefined,
    endType: (row.end_type as 'date' | 'occurrences') ?? undefined,
    endDate: row.end_date ?? undefined,
    occurrences: row.occurrences ?? undefined,
    daySchedules: (row as any).day_schedules ?? null,
    status: (row.status as 'active' | 'draft' | 'cancelled') ?? 'draft',
    createdAt: toDate(row.created_at) ?? new Date(),
    updatedAt: toDate(row.updated_at) ?? new Date(),
    importConfidence: (row as any).import_confidence ?? undefined,
    importSource: (row as any).import_source ?? undefined,
  };
};

// Exports pour compatibilité avec l'ancien service
export const getStableDate = (timestamp: number): Date => {
  return EventNormalizationCache.getStableDate(timestamp);
};

export const cleanupNormalizationCache = (): void => {
  EventNormalizationCache.cleanup();
};

export const getNormalizationCacheStats = () => {
  return EventNormalizationCache.getStats();
};

export const initializeNormalizationCache = (): void => {
  EventNormalizationCache.initializeCleanup();
};

export const stopNormalizationCache = (): void => {
  EventNormalizationCache.stopCleanup();
};

export const clearNormalizationCache = (): void => {
  EventNormalizationCache.clearAll();
};

interface ListenerConsumer {
  id: string;
  callback: (events: Event[]) => void;
}

interface SeriesConsumer {
  id: string;
  callback: (series: EventSeries[]) => void;
}

interface ChannelInfo {
  channel: RealtimeChannel;
  pausedAt?: number;
}

// 🔧 Pour stocker les paramètres géo afin de pouvoir recharger les données fraîches
interface GeoChannelParams {
  center: { lat: number; lng: number };
  radius: number;
  geohashes?: string[]; // Liste des geohash couverts (centre + 8 voisins)
}

/**
 * 🔍 COMPARAISON EFFICACE - Évite les notifications inutiles
 * Compare les arrays par IDs et timestamps de mise à jour
 */
const hasEventsChanged = (prevEvents: Event[], newEvents: Event[]): boolean => {
  // Changement de taille = changement certain
  if (prevEvents.length !== newEvents.length) return true;

  // Comparer par ID et updatedAt (rapide et fiable)
  const prevMap = new Map(prevEvents.map(e => [e.id, e.updatedAt?.getTime() ?? 0]));

  for (const event of newEvents) {
    const prevTimestamp = prevMap.get(event.id);
    // ID absent ou timestamp différent = changement
    if (prevTimestamp === undefined) return true;
    const newTimestamp = event.updatedAt?.getTime() ?? 0;
    if (prevTimestamp !== newTimestamp) return true;
  }

  return false;
};

const hasSeriesChanged = (prevSeries: EventSeries[], newSeries: EventSeries[]): boolean => {
  if (prevSeries.length !== newSeries.length) return true;

  const prevMap = new Map(prevSeries.map(s => [s.id, s.updatedAt?.getTime() ?? 0]));

  for (const series of newSeries) {
    const prevTimestamp = prevMap.get(series.id);
    if (prevTimestamp === undefined) return true;
    const newTimestamp = series.updatedAt?.getTime() ?? 0;
    if (prevTimestamp !== newTimestamp) return true;
  }

  return false;
};

/**
 * 🛡️ FIX PRD Fiabilité découverte (item 1) — Exécute une écriture cache (IndexedDB)
 * sans jamais bloquer/perturber le chemin réseau qui l'entoure.
 * Le cache est une OPTIMISATION, jamais une source de vérité : un échec d'écriture
 * (quota dépassé, navigation privée Safari qui bloque IndexedDB, etc.) est loggé et
 * avalé ici, au lieu de remonter comme une erreur de "chargement" côté appelant.
 * Fonction module-level (et non méthode de classe) pour être réutilisable par
 * `getEventById` en plus de `SmartListenerService`.
 */
const safeCacheWrite = async (write: () => Promise<unknown>, label: string): Promise<void> => {
  try {
    await write();
  } catch (error) {
    console.warn(`⚠️ [SmartListener-Cache] Écriture cache échouée (${label}), on continue sans cache:`, error);
  }
};

/**
 * SmartListenerService pour Supabase Realtime
 * Utilise postgres_changes au lieu de Firestore onSnapshot
 */
class SmartListenerService {
  private static instance: SmartListenerService;

  // Channels actifs (Supabase Realtime)
  private activeGeoChannels = new Map<string, ChannelInfo>();
  private activeOrganizerChannels = new Map<string, ChannelInfo>();
  private activeSeriesChannels = new Map<string, ChannelInfo>();

  // 🔧 FIX RACE CONDITION: Channels en cours de création (évite les duplications)
  private pendingGeoChannels = new Set<string>();
  private pendingOrganizerChannels = new Set<string>();
  private pendingSeriesChannels = new Set<string>();

  // Consumers
  private geoConsumers = new Map<string, Set<ListenerConsumer>>();
  private organizerConsumers = new Map<string, Set<ListenerConsumer>>();
  private seriesConsumers = new Map<string, Set<SeriesConsumer>>();

  // Channels en pause
  private pausedGeoChannels = new Map<string, ChannelInfo>();
  private pausedOrganizerChannels = new Map<string, ChannelInfo>();
  private pausedSeriesChannels = new Map<string, ChannelInfo>();

  // TTL fixes pour les listeners
  private static readonly PAUSED_LISTENER_TTL = 5 * 24 * 60 * 60 * 1000; // 5 jours
  private static readonly BACKGROUND_PAUSED_TTL = 10 * 24 * 60 * 60 * 1000; // 10 jours
  private static readonly BACKGROUND_PAUSE_DELAY = 5 * 60 * 1000; // 5 minutes

  // Détection de visibilité PWA
  private visibilityTimeoutId: number | null = null;
  private isAppVisible = true;
  private interactionListenersSetup = false;

  // Cache des dernières données pour initial load
  private lastGeoData = new Map<string, Event[]>();
  private lastOrganizerData = new Map<string, Event[]>();
  private lastSeriesData = new Map<string, EventSeries[]>();

  // 🔧 FIX: Cache des données déjà notifiées (évite notifications inutiles)
  private lastNotifiedGeoData = new Map<string, Event[]>();
  private lastNotifiedOrganizerData = new Map<string, Event[]>();
  private lastNotifiedSeriesData = new Map<string, EventSeries[]>();

  // 🔧 Stocker les paramètres géo pour rechargement frais
  private geoChannelParams = new Map<string, GeoChannelParams>();

  private constructor() {
    this.setupVisibilityDetection();
  }

  static getInstance(): SmartListenerService {
    if (!SmartListenerService.instance) {
      SmartListenerService.instance = new SmartListenerService();
    }
    return SmartListenerService.instance;
  }

  /**
   * 🗺️ Calcule le geohash central et ses 8 voisins
   * ngeohash.neighbors() retourne un array: [n, ne, e, se, s, sw, w, nw]
   */
  private calculateGeohashZone(lat: number, lng: number, radius: number): GeohashZoneInfo {
    const centerHash = geohash.encode(lat, lng, GEOHASH_PRECISION);
    const neighborHashes = geohash.neighbors(centerHash);

    // Centre + 8 voisins = 9 zones couvertes
    // neighbors retourne [n, ne, e, se, s, sw, w, nw]
    const allHashes = [centerHash, ...neighborHashes];

    return {
      centerHash,
      allHashes,
      center: { lat, lng },
      radius
    };
  }

  /**
   * 🌍 ABONNEMENT GEO - Version Geohash avec fusion des zones adjacentes
   * Utilise le geohash central comme cle pour eviter la creation de multiples zones
   */
  subscribeToGeoEvents(
    latitude: number,
    longitude: number,
    radius: number,
    consumerId: string,
    callback: (events: Event[]) => void
  ): () => void {
    // Calculer la zone geohash (centre + 8 voisins)
    const zoneInfo = this.calculateGeohashZone(latitude, longitude, radius);
    const { centerHash, allHashes } = zoneInfo;

    // Utiliser le centerHash comme cle principale (stabilite!)
    const zoneKey = `geo_${centerHash}_${radius}`;

    console.log(`🔔 [SmartListener-Geohash] Consumer ${consumerId} s'abonne a zone ${centerHash} (+8 voisins)`);

    // Stocker les parametres pour rechargement
    this.geoChannelParams.set(zoneKey, {
      center: { lat: latitude, lng: longitude },
      radius,
      geohashes: allHashes
    });

    // Ajouter le consumer
    if (!this.geoConsumers.has(zoneKey)) {
      this.geoConsumers.set(zoneKey, new Set());
    }
    this.geoConsumers.get(zoneKey)!.add({ id: consumerId, callback });

    // Verifier si un channel est en pause ou actif
    const pausedChannel = this.pausedGeoChannels.get(zoneKey);
    if (pausedChannel) {
      console.log(`🔄 [SmartListener-Geohash] Reveil channel en pause pour zone ${centerHash}`);
      this.activeGeoChannels.set(zoneKey, pausedChannel);
      this.pausedGeoChannels.delete(zoneKey);

      // Recharger les donnees fraiches
      this.loadInitialGeoEventsByGeohash(zoneKey, zoneInfo);
    } else if (!this.activeGeoChannels.has(zoneKey) && !this.pendingGeoChannels.has(zoneKey)) {
      console.log(`🔥 [SmartListener-Geohash] Creation NOUVEAU channel pour zone ${centerHash}`);
      this.pendingGeoChannels.add(zoneKey);
      this.startGeoChannelByGeohash(zoneKey, zoneInfo);
    } else if (this.pendingGeoChannels.has(zoneKey)) {
      console.log(`⏳ [SmartListener-Geohash] Channel geo ${centerHash} en cours de création, attente...`);
      // Le callback sera notifié quand le channel sera prêt via notifyGeoConsumers
    } else {
      console.log(`♻️ [SmartListener-Geohash] Reutilisation channel existant pour zone ${centerHash}`);
      const lastData = this.lastGeoData.get(zoneKey);
      if (lastData) {
        callback(lastData);
      }
    }

    return () => this.unsubscribeFromGeoEvents(zoneKey, consumerId);
  }

  /**
   * 🏢 ABONNEMENT ORGANIZER - Partage channel entre composants
   */
  subscribeToOrganizerEvents(
    organizerId: string,
    consumerId: string,
    callback: (events: Event[]) => void
  ): () => void {
    console.log(`🔔 [SmartListener-Supabase] Consumer ${consumerId} s'abonne à organizer ${organizerId}`);

    if (!this.organizerConsumers.has(organizerId)) {
      this.organizerConsumers.set(organizerId, new Set());
    }
    this.organizerConsumers.get(organizerId)!.add({ id: consumerId, callback });

    const pausedChannel = this.pausedOrganizerChannels.get(organizerId);
    if (pausedChannel) {
      console.log(`🔄 [SmartListener-Supabase] Réveil channel organisateur en pause ${organizerId}`);
      this.activeOrganizerChannels.set(organizerId, pausedChannel);
      this.pausedOrganizerChannels.delete(organizerId);

      // 🔧 FIX CRITIQUE: Recharger les données FRAÎCHES depuis Supabase
      // au lieu d'envoyer les données cachées potentiellement stales
      console.log(`🔄 [SmartListener-Supabase] Rechargement données fraîches pour organisateur ${organizerId}`);
      this.loadInitialOrganizerEvents(organizerId);
      // Le callback sera notifié via notifyOrganizerConsumers après le chargement
    } else if (!this.activeOrganizerChannels.has(organizerId) && !this.pendingOrganizerChannels.has(organizerId)) {
      console.log(`🔥 [SmartListener-Supabase] Création NOUVEAU channel pour organizer ${organizerId}`);
      this.pendingOrganizerChannels.add(organizerId);
      this.startOrganizerChannel(organizerId);
    } else if (this.pendingOrganizerChannels.has(organizerId)) {
      console.log(`⏳ [SmartListener-Supabase] Channel organizer ${organizerId} en cours de création, attente...`);
      // Le callback sera notifié quand le channel sera prêt via notifyOrganizerConsumers
    } else {
      console.log(`♻️ [SmartListener-Supabase] Réutilisation channel existant pour organizer ${organizerId}`);
      const lastData = this.lastOrganizerData.get(organizerId);
      if (lastData) {
        callback(lastData);
      }
    }

    return () => this.unsubscribeFromOrganizerEvents(organizerId, consumerId);
  }

  /**
   * 🔄 ABONNEMENT SÉRIES - Partage channel entre composants
   */
  subscribeToSeries(
    organizerId: string,
    consumerId: string,
    callback: (series: EventSeries[]) => void
  ): () => void {
    console.log(`🔔 [SmartListener-Supabase] Consumer ${consumerId} s'abonne aux séries ${organizerId}`);

    if (!this.seriesConsumers.has(organizerId)) {
      this.seriesConsumers.set(organizerId, new Set());
    }
    this.seriesConsumers.get(organizerId)!.add({ id: consumerId, callback });

    const pausedChannel = this.pausedSeriesChannels.get(organizerId);
    if (pausedChannel) {
      console.log(`🔄 [SmartListener-Supabase] Réveil channel séries en pause ${organizerId}`);
      this.activeSeriesChannels.set(organizerId, pausedChannel);
      this.pausedSeriesChannels.delete(organizerId);

      // 🔧 FIX CRITIQUE: Recharger les données FRAÎCHES depuis Supabase
      console.log(`🔄 [SmartListener-Supabase] Rechargement séries fraîches pour organisateur ${organizerId}`);
      this.loadInitialSeries(organizerId);
      // Le callback sera notifié via notifySeriesConsumers après le chargement
    } else if (!this.activeSeriesChannels.has(organizerId) && !this.pendingSeriesChannels.has(organizerId)) {
      console.log(`🔥 [SmartListener-Supabase] Création NOUVEAU channel pour séries ${organizerId}`);
      this.pendingSeriesChannels.add(organizerId);
      this.startSeriesChannel(organizerId);
    } else if (this.pendingSeriesChannels.has(organizerId)) {
      console.log(`⏳ [SmartListener-Supabase] Channel séries ${organizerId} en cours de création, attente...`);
      // Le callback sera notifié quand le channel sera prêt via notifySeriesConsumers
    } else {
      console.log(`♻️ [SmartListener-Supabase] Réutilisation channel existant pour séries ${organizerId}`);
      const lastData = this.lastSeriesData.get(organizerId);
      if (lastData) {
        callback(lastData);
      }
    }

    return () => this.unsubscribeFromSeries(organizerId, consumerId);
  }

  /**
   * 🔥 CHANNEL GEO GEOHASH - Version optimisee avec geohash
   * Demarre un channel Realtime pour une zone geohash (centre + 8 voisins)
   */
  private async startGeoChannelByGeohash(zoneKey: string, zoneInfo: GeohashZoneInfo, retryCount: number = 0) {
    console.log(`🔥 [SmartListener-Geohash] Demarrage channel geo zone ${zoneInfo.centerHash} (tentative ${retryCount + 1})`);

    // Charger les donnees initiales via la nouvelle RPC geohash
    await this.loadInitialGeoEventsByGeohash(zoneKey, zoneInfo);

    // 🔧 FIX PRD Fiabilité découverte (item 3) : évite un 2e retry en double pour ce même channel
    // (ex: CLOSED tardif après CHANNEL_ERROR déjà traité, ou CLOSED déclenché par notre propre
    // removeChannel de retry ci-dessous)
    let isSuperseded = false;

    // 🔧 FIX: Retirer le filtre status=eq.active pour recevoir les events cancelled
    // On filtre côté client pour ignorer les drafts uniquement
    // Nom du channel horodaté (comme organizer/series) pour éviter une collision de nom
    // lors d'une reconnexion (l'ancien channel n'est pas toujours retiré synchronement)
    const channel = supabase
      .channel(`geo-events-${zoneInfo.centerHash}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events'
          // Plus de filtre status - on reçoit active + cancelled (pas draft)
        },
        async (payload: RealtimePostgresChangesPayload<DbEventRow>) => {
          const newData = payload.new as DbEventRow | null;

          // Filtrer côté client : ignorer les drafts (non publiés)
          if (newData && newData.status === 'draft') {
            console.log(`🔇 [SmartListener-Geohash] Event ignoré (status=draft)`);
            return;
          }

          // Verifier si l'event est dans nos geohash
          if (newData && newData.geohash) {
            const isInZone = zoneInfo.allHashes.some(hash =>
              newData.geohash?.startsWith(hash)
            );

            if (isInZone) {
              console.log(`📡 [SmartListener-Geohash] Event dans zone ${zoneInfo.centerHash}:`, payload.eventType);
              await this.handleGeoEventChangeByGeohash(zoneKey, zoneInfo, payload);
            }
          } else if (newData && newData.latitude && newData.longitude) {
            // Fallback: verifier par distance si geohash absent
            const distance = this.calculateDistance(
              zoneInfo.center.lat, zoneInfo.center.lng,
              newData.latitude, newData.longitude
            );
            if (distance <= zoneInfo.radius * 1000) {
              console.log(`📡 [SmartListener-Geohash] Event dans rayon (sans geohash):`, payload.eventType);
              await this.handleGeoEventChangeByGeohash(zoneKey, zoneInfo, payload);
            }
          }
        }
      )
      .subscribe((status, err) => {
        console.log(`📊 [SmartListener-Geohash] Status channel geo ${zoneInfo.centerHash}:`, status, err ? err : '');

        if (status === 'SUBSCRIBED') {
          console.log(`✅ [SmartListener-Geohash] Channel geo ${zoneInfo.centerHash} actif via Realtime`);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // 🔧 FIX PRD Fiabilité découverte (item 3) : le canal géo (celui que TOUS les touristes
          // utilisent) n'avait aucun retry/fallback — une coupure le laissait mort jusqu'au reload.
          // On réplique ici le pattern déjà en place pour organizer/series : backoff exponentiel
          // puis fallback polling, avec gestion de TIMED_OUT/CLOSED en plus de CHANNEL_ERROR.
          if (isSuperseded) return;

          // Ne PAS reconnecter si ce channel a été fermé volontairement (plus aucun consumer —
          // pause TTL, destruction background après 5/10 jours d'inactivité)
          const consumers = this.geoConsumers.get(zoneKey);
          if (!consumers || consumers.size === 0) {
            console.log(`ℹ️ [SmartListener-Geohash] Channel geo ${zoneInfo.centerHash} fermé (${status}) sans consumer actif, pas de reconnexion`);
            return;
          }

          isSuperseded = true;
          console.error(`❌ [SmartListener-Geohash] Channel geo ${zoneInfo.centerHash} en erreur (${status}):`, err);

          if (retryCount < 2) {
            const delay = Math.pow(2, retryCount) * 1000;
            console.log(`🔄 [SmartListener-Geohash] Retry dans ${delay}ms...`);
            setTimeout(() => {
              supabase.removeChannel(channel);
              this.startGeoChannelByGeohash(zoneKey, zoneInfo, retryCount + 1);
            }, delay);
          } else {
            console.warn(`⚠️ [SmartListener-Geohash] Realtime indisponible pour zone ${zoneInfo.centerHash} - activation du polling (5s)`);
            this.pendingGeoChannels.delete(zoneKey); // 🔧 FIX RACE CONDITION (fallback polling)
            this.startGeoPolling(zoneKey, zoneInfo);
          }
        }
      });

    this.activeGeoChannels.set(zoneKey, { channel });
    this.pendingGeoChannels.delete(zoneKey); // 🔧 FIX RACE CONDITION
  }

  /**
   * ⏰ FIX PRD Fiabilité découverte (item 3) — FALLBACK POLLING si Realtime échoue pour le canal géo
   * Réplique le pattern startOrganizerPolling/startSeriesPolling.
   */
  private geoPollingIntervals = new Map<string, NodeJS.Timeout>();

  private startGeoPolling(zoneKey: string, zoneInfo: GeohashZoneInfo) {
    // Éviter les doublons
    if (this.geoPollingIntervals.has(zoneKey)) {
      return;
    }

    console.log(`⏰ [SmartListener-Geohash] Démarrage polling zone ${zoneInfo.centerHash} (5s)`);

    const pollFn = async () => {
      const consumers = this.geoConsumers.get(zoneKey);
      if (!consumers || consumers.size === 0) {
        console.log(`🛑 [SmartListener-Geohash] Arrêt polling - plus de consumers`);
        this.stopGeoPolling(zoneKey);
        return;
      }
      await this.loadInitialGeoEventsByGeohash(zoneKey, zoneInfo);
    };

    // Premier poll immédiat
    pollFn();

    // Puis toutes les 5 secondes
    const interval = setInterval(pollFn, 5000);
    this.geoPollingIntervals.set(zoneKey, interval);
  }

  private stopGeoPolling(zoneKey: string) {
    const interval = this.geoPollingIntervals.get(zoneKey);
    if (interval) {
      clearInterval(interval);
      this.geoPollingIntervals.delete(zoneKey);
      console.log(`🛑 [SmartListener-Geohash] Polling stoppé pour zone ${zoneKey}`);
    }
  }

  /**
   * 📦 Charge les events initiaux via la fonction RPC geohash
   */
  private async loadInitialGeoEventsByGeohash(zoneKey: string, zoneInfo: GeohashZoneInfo, retryAttempt: number = 0) {
    const MAX_RETRIES = 2;
    let events: Event[];

    // 🔧 FIX PRD Fiabilité découverte (item 1) : SEUL l'appel réseau est dans ce try/catch.
    // Auparavant, les écritures cache IndexedDB étaient mélangées ici : un échec de
    // `saveGeoEvent`/`removeStaleGeoEvents` (ex: navigation privée Safari qui rejette
    // IndexedDB) était traité comme un échec de chargement RPC → 2 retries inutiles →
    // `notifyGeoConsumers(zoneKey, [])` = liste vide alors que le RPC avait réussi.
    try {
      console.log(`📦 [SmartListener-Geohash] Chargement events pour zone ${zoneInfo.centerHash} (+8 voisins)${retryAttempt > 0 ? ` (retry ${retryAttempt}/${MAX_RETRIES})` : ''}`);

      // Note: get_events_by_geohashes est une fonction RPC custom (migration 006)
      // On utilise un cast car elle n'est pas dans les types generes automatiquement
      const { data, error } = await (supabase.rpc as any)('get_events_by_geohashes', {
        p_geohashes: zoneInfo.allHashes,
        p_center_lat: zoneInfo.center.lat,
        p_center_lng: zoneInfo.center.lng,
        p_radius_meters: zoneInfo.radius * 1000,
        p_min_end_time: new Date().toISOString(),
      });

      if (error) throw error;

      events = ((data as any[]) ?? []).map((row: any) => normalizeSupabaseEvent(row as DbEventRow));

      console.log(`✅ [SmartListener-Geohash] ${events.length} events charges pour zone ${zoneInfo.centerHash}`);
    } catch (error) {
      console.error(`❌ [SmartListener-Geohash] Erreur chargement geo ${zoneInfo.centerHash}:`, error);

      // FALLBACK: Utiliser l'ancienne methode RPC si la nouvelle echoue
      console.log(`⚠️ [SmartListener-Geohash] Fallback vers get_events_near_location`);
      try {
        await this.loadInitialGeoEvents(zoneKey, zoneInfo.center, zoneInfo.radius);
      } catch (fallbackError) {
        console.error(`❌ [SmartListener-Geohash] Fallback aussi échoué:`, fallbackError);

        // 🔧 FIX: Retry avec backoff si les deux RPC ont échoué (réseau lent)
        if (retryAttempt < MAX_RETRIES) {
          const delay = (retryAttempt + 1) * 3000; // 3s, 6s
          console.log(`🔄 [SmartListener-Geohash] Retry dans ${delay / 1000}s...`);
          setTimeout(() => {
            this.loadInitialGeoEventsByGeohash(zoneKey, zoneInfo, retryAttempt + 1);
          }, delay);
        } else {
          // 🔧 FIX: Notifier les consumers même en cas d'échec total
          // pour débloquer le loading (sinon skeletons infinis)
          console.error(`❌ [SmartListener-Geohash] Abandon après ${MAX_RETRIES} retries`);
          this.notifyGeoConsumers(zoneKey, []);
        }
      }
      return;
    }

    // ✅ Réseau OK à partir d'ici. Le cache est une optimisation : chaque écriture est
    // isolée via safeCacheWrite pour qu'un échec IndexedDB ne bloque jamais la notification
    // des events réellement reçus du serveur.
    for (const event of events) {
      if (event.latitude && event.longitude) {
        await safeCacheWrite(() => locationCache.saveGeoEvent(event, zoneKey), `saveGeoEvent(${event.id})`);
      }
    }

    // 🧹 Purger les events périmés du cache (supprimés de la BDD, séries recréées...)
    // Garde de sécurité : ne pas purger si la RPC retourne 0 events (possible erreur réseau)
    if (events.length > 0) {
      const freshEventIds = new Set(events.map(e => e.id));
      await safeCacheWrite(
        () => locationCache.removeStaleGeoEvents(freshEventIds, zoneInfo.allHashes),
        'removeStaleGeoEvents'
      );
    }

    // Marquer timestamp listener actif (localStorage peut aussi être bloqué en navigation privée)
    try {
      localStorage.setItem(`geo_listener_${zoneKey}`, Date.now().toString());
    } catch (storageError) {
      console.warn(`⚠️ [SmartListener-Geohash] localStorage indisponible (navigation privée ?):`, storageError);
    }

    // Stocker et notifier avec les events RÉELLEMENT retournés par le RPC
    this.lastGeoData.set(zoneKey, events);
    this.notifyGeoConsumers(zoneKey, events);
  }

  /**
   * 🔄 Gerer un changement d'event de maniere incrementale (version geohash)
   */
  private async handleGeoEventChangeByGeohash(
    zoneKey: string,
    zoneInfo: GeohashZoneInfo,
    payload: RealtimePostgresChangesPayload<DbEventRow>
  ) {
    const currentEvents = this.lastGeoData.get(zoneKey) || [];
    let updatedEvents = [...currentEvents];

    const eventType = payload.eventType;
    const newData = payload.new as DbEventRow | null;
    const oldData = payload.old as { id: string } | null;

    console.log(`🔄 [SmartListener-Geohash] Traitement ${eventType} pour zone ${zoneInfo.centerHash}`);

    if (eventType === 'INSERT' && newData) {
      const normalizedEvent = normalizeSupabaseEvent(newData);
      // Realtime peut livrer un meme INSERT deux fois (redelivrance, resouscription)
      const existingIndex = updatedEvents.findIndex(e => e.id === newData.id);
      if (existingIndex !== -1) {
        updatedEvents[existingIndex] = normalizedEvent;
        console.log(`♻️ [SmartListener-Geohash] INSERT deja connu, remplace: ${newData.title}`);
      } else {
        updatedEvents.push(normalizedEvent);
        console.log(`➕ [SmartListener-Geohash] Event ajoute: ${newData.title}`);
      }

      // Sauvegarder dans IndexedDB (best-effort — ne doit jamais bloquer la notification realtime)
      await safeCacheWrite(() => locationCache.saveGeoEvent(normalizedEvent, zoneKey), `saveGeoEvent(${normalizedEvent.id})`);
    } else if (eventType === 'UPDATE' && newData) {
      const index = updatedEvents.findIndex(e => e.id === newData.id);
      const normalizedEvent = normalizeSupabaseEvent(newData);

      if (index !== -1) {
        updatedEvents[index] = normalizedEvent;
        console.log(`✏️ [SmartListener-Geohash] Event mis a jour: ${newData.title}`);
      } else {
        // L'event peut etre entre dans la zone (changement de position)
        updatedEvents.push(normalizedEvent);
        console.log(`➕ [SmartListener-Geohash] Event entre dans la zone: ${newData.title}`);
      }

      await safeCacheWrite(() => locationCache.saveGeoEvent(normalizedEvent, zoneKey), `saveGeoEvent(${normalizedEvent.id})`);

      // Sync favoris si necessaire
      await this.syncFavoriteIfNeeded(normalizedEvent);
    } else if (eventType === 'DELETE' && oldData) {
      updatedEvents = updatedEvents.filter(e => e.id !== oldData.id);
      console.log(`🗑️ [SmartListener-Geohash] Event supprime: ${oldData.id}`);
    }

    // Mettre a jour le cache local et notifier
    this.lastGeoData.set(zoneKey, updatedEvents);
    this.notifyGeoConsumers(zoneKey, updatedEvents);
  }


  /**
   * 📏 Calcul de distance Haversine (en mètres)
   */
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Rayon de la Terre en mètres
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * ❤️ SYNC FAVORIS - Mettre à jour un favori si l'event modifié est en favoris
   */
  private async syncFavoriteIfNeeded(event: Event): Promise<void> {
    try {
      // Récupérer les favoris depuis IndexedDB
      const favoriteEvents = await locationCache.getFavoriteEventsFromDB();

      // Vérifier si l'event (par ID ou seriesId) est dans les favoris
      const storageId = event.seriesId || event.id;
      const isFavorite = favoriteEvents.some((fav: Event) => {
        const favStorageId = fav.seriesId || fav.id;
        return fav.id === event.id || favStorageId === storageId;
      });

      if (isFavorite) {
        console.log(`❤️ [SmartListener-Supabase] Event favori détecté, mise à jour: ${event.title}`);

        // Mettre à jour le favori dans IndexedDB
        await locationCache.saveFavoriteEvent(event);

        // Émettre l'événement pour déclencher le refresh des favoris
        window.dispatchEvent(new CustomEvent('favoritesChanged', {
          detail: { eventId: event.id, action: 'updated' }
        }));

        console.log(`✅ [SmartListener-Supabase] Favori synchronisé: ${event.title}`);
      }
    } catch (error) {
      console.error('❌ [SmartListener-Supabase] Erreur sync favori:', error);
    }
  }

  /**
   * 📦 Charger les événements géo initiaux
   */
  private async loadInitialGeoEvents(zoneKey: string, center: { lat: number; lng: number }, radius: number) {
    // 🔧 FIX PRD Fiabilité découverte (item 1) : seul l'appel réseau est susceptible de
    // faire échouer cette fonction (et donc son retry côté appelant). Les écritures cache
    // ci-dessous sont isolées via safeCacheWrite — même politique que loadInitialGeoEventsByGeohash.
    let events: Event[];
    try {
      // Utiliser la fonction RPC PostGIS
      const { data, error } = await supabase.rpc('get_events_near_location', {
        p_lat: center.lat,
        p_lng: center.lng,
        p_radius_meters: radius * 1000,
        p_min_end_time: new Date().toISOString(),
      });

      if (error) throw error;

      events = (data ?? []).map((row: any) => normalizeSupabaseEvent(row as DbEventRow));

      console.log(`📦 [SmartListener-Supabase] ${events.length} events chargés pour zone ${zoneKey}`);
    } catch (error) {
      console.error(`❌ [SmartListener-Supabase] Erreur chargement géo ${zoneKey}:`, error);
      // 🔧 FIX: Propager l'erreur réseau pour que le caller puisse retry
      throw error;
    }

    // Sauvegarder dans le cache (best-effort, jamais bloquant)
    for (const event of events) {
      if (event.latitude && event.longitude) {
        await safeCacheWrite(() => locationCache.saveGeoEvent(event, zoneKey), `saveGeoEvent(${event.id})`);
      }
    }

    // 🧹 Purger les events périmés du cache (fallback sans geohash)
    // Garde de sécurité : ne pas purger si la RPC retourne 0 events (possible erreur réseau)
    if (events.length > 0) {
      const centerHash = geohash.encode(center.lat, center.lng, 5);
      const neighborHashes = geohash.neighbors(centerHash);
      const allHashes = [centerHash, ...neighborHashes];
      const freshEventIds = new Set(events.map(e => e.id));
      await safeCacheWrite(
        () => locationCache.removeStaleGeoEvents(freshEventIds, allHashes),
        'removeStaleGeoEvents'
      );
    }

    // Marquer timestamp listener actif
    try {
      localStorage.setItem(`geo_listener_${zoneKey}`, Date.now().toString());
    } catch (storageError) {
      console.warn(`⚠️ [SmartListener-Supabase] localStorage indisponible (navigation privée ?):`, storageError);
    }

    // Stocker les dernières données
    this.lastGeoData.set(zoneKey, events);

    // Notifier les consumers
    this.notifyGeoConsumers(zoneKey, events);
  }

  /**
   * 🔥 CHANNEL ORGANIZER PRIVÉ
   * ✅ OPTIMISÉ: Utilise payload.new au lieu de tout retélécharger
   */
  private async startOrganizerChannel(organizerId: string, retryCount: number = 0) {
    console.log(`🔥 [SmartListener-Supabase] Démarrage channel organisateur ${organizerId} (tentative ${retryCount + 1})`);

    // Charger les données initiales
    await this.loadInitialOrganizerEvents(organizerId);

    // 🔧 DEBUG: Vérifier l'état d'authentification
    const { data: { session } } = await supabase.auth.getSession();
    console.log(`🔐 [SmartListener-Supabase] Auth check:`, {
      hasSession: !!session,
      userId: session?.user?.id?.substring(0, 8) + '...',
      organizerId: organizerId.substring(0, 8) + '...',
      match: session?.user?.id === organizerId
    });

    // Créer le channel Realtime avec filtre sur organizer_id
    const channelName = `organizer-events-${organizerId}-${Date.now()}`;
    // 🔧 FIX PRD Fiabilité découverte (item 3) : évite qu'un 2e statut d'échec (ex: CLOSED
    // juste après CHANNEL_ERROR, ou CLOSED déclenché par notre propre removeChannel de retry)
    // ne déclenche un 2e retry en double pour ce même channel.
    let isSuperseded = false;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: `organizer_id=eq.${organizerId}`
        },
        async (payload: RealtimePostgresChangesPayload<DbEventRow>) => {
          console.log(`📡 [SmartListener-Supabase] Changement détecté organisateur ${organizerId}:`, payload.eventType);
          // ✅ OPTIMISATION: Utiliser payload.new au lieu de tout retélécharger
          await this.handleOrganizerEventChange(organizerId, payload);
        }
      )
      .subscribe((status, err) => {
        console.log(`📊 [SmartListener-Supabase] Status channel organisateur: ${status}`, err ? err : '');

        if (status === 'SUBSCRIBED') {
          console.log(`✅ [SmartListener-Supabase] Channel organisateur ${organizerId} actif via Realtime`);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          if (isSuperseded) return;

          // 🔧 FIX PRD Fiabilité découverte (item 3) : ne PAS reconnecter si ce channel a été
          // fermé volontairement (plus aucun consumer — pause TTL, destruction background).
          const consumers = this.organizerConsumers.get(organizerId);
          if (!consumers || consumers.size === 0) {
            console.log(`ℹ️ [SmartListener-Supabase] Channel organisateur ${organizerId} fermé (${status}) sans consumer actif, pas de reconnexion`);
            return;
          }

          isSuperseded = true;
          console.error(`❌ [SmartListener-Supabase] Channel organisateur ${organizerId} en erreur (${status}):`, err);

          // 🔧 FIX: Activer le mode polling comme fallback
          if (retryCount < 2) {
            const delay = Math.pow(2, retryCount) * 1000;
            console.log(`🔄 [SmartListener-Supabase] Retry dans ${delay}ms...`);
            setTimeout(() => {
              supabase.removeChannel(channel);
              this.startOrganizerChannel(organizerId, retryCount + 1);
            }, delay);
          } else {
            console.warn(`⚠️ [SmartListener-Supabase] Realtime indisponible - activation du polling (5s)`);
            this.pendingOrganizerChannels.delete(organizerId); // 🔧 FIX RACE CONDITION (fallback polling)
            this.startOrganizerPolling(organizerId);
          }
        }
      });

    this.activeOrganizerChannels.set(organizerId, { channel });
    this.pendingOrganizerChannels.delete(organizerId); // 🔧 FIX RACE CONDITION
  }

  /**
   * ⏰ FALLBACK POLLING si Realtime échoue
   */
  private organizerPollingIntervals = new Map<string, NodeJS.Timeout>();

  private startOrganizerPolling(organizerId: string) {
    // Éviter les doublons
    if (this.organizerPollingIntervals.has(organizerId)) {
      return;
    }

    console.log(`⏰ [SmartListener-Supabase] Démarrage polling organisateur ${organizerId} (5s)`);

    const pollFn = async () => {
      const consumers = this.organizerConsumers.get(organizerId);
      if (!consumers || consumers.size === 0) {
        console.log(`🛑 [SmartListener-Supabase] Arrêt polling - plus de consumers`);
        this.stopOrganizerPolling(organizerId);
        return;
      }
      await this.loadInitialOrganizerEvents(organizerId);
    };

    // Premier poll immédiat
    pollFn();

    // Puis toutes les 5 secondes
    const interval = setInterval(pollFn, 5000);
    this.organizerPollingIntervals.set(organizerId, interval);
  }

  private stopOrganizerPolling(organizerId: string) {
    const interval = this.organizerPollingIntervals.get(organizerId);
    if (interval) {
      clearInterval(interval);
      this.organizerPollingIntervals.delete(organizerId);
      console.log(`🛑 [SmartListener-Supabase] Polling stoppé pour ${organizerId}`);
    }
  }

  /**
   * 🔄 Gérer un changement d'event organisateur de manière incrémentale
   */
  private async handleOrganizerEventChange(
    organizerId: string,
    payload: RealtimePostgresChangesPayload<DbEventRow>
  ) {
    const currentEvents = this.lastOrganizerData.get(organizerId) || [];
    let updatedEvents = [...currentEvents];

    const eventType = payload.eventType;
    const newData = payload.new as DbEventRow | null;
    const oldData = payload.old as { id: string } | null;

    console.log(`🔄 [SmartListener-Supabase] Traitement ${eventType} pour organisateur ${organizerId}`);

    if (eventType === 'INSERT' && newData) {
      const normalizedEvent = normalizeSupabaseEvent(newData);
      // Realtime peut livrer un même INSERT deux fois (redélivrance, resouscription)
      const existingIndex = updatedEvents.findIndex(e => e.id === newData.id);
      if (existingIndex !== -1) {
        updatedEvents[existingIndex] = normalizedEvent;
        console.log(`♻️ [SmartListener-Supabase] INSERT déjà connu, remplacé: ${newData.title}`);
      } else {
        updatedEvents.push(normalizedEvent);
        console.log(`➕ [SmartListener-Supabase] Event ajouté: ${newData.title}`);
      }

      // Sauvegarder dans IndexedDB
      // safeCacheWrite obligatoire : ce handler est appelé par le callback realtime
      // Supabase, qui n'attend ni ne catche les promesses — un rejet IndexedDB ici
      // partirait en unhandledrejection (cf. Sentry WHAT2DO-WEB-7).
      await safeCacheWrite(() => locationCache.saveOrganizerEvent(normalizedEvent), `saveOrganizerEvent(${normalizedEvent.id})`);
    } else if (eventType === 'UPDATE' && newData) {
      const index = updatedEvents.findIndex(e => e.id === newData.id);
      const normalizedEvent = normalizeSupabaseEvent(newData);

      if (index !== -1) {
        updatedEvents[index] = normalizedEvent;
        console.log(`✏️ [SmartListener-Supabase] Event mis à jour: ${newData.title}`);
      } else {
        // Event pas encore dans la liste (peut arriver si filtre a changé)
        updatedEvents.push(normalizedEvent);
        console.log(`➕ [SmartListener-Supabase] Event ajouté (UPDATE): ${newData.title}`);
      }

      await safeCacheWrite(() => locationCache.saveOrganizerEvent(normalizedEvent), `saveOrganizerEvent(${normalizedEvent.id})`);

      // ✅ SYNC FAVORIS: Mettre à jour si l'event est en favoris
      await this.syncFavoriteIfNeeded(normalizedEvent);
    } else if (eventType === 'DELETE' && oldData) {
      updatedEvents = updatedEvents.filter(e => e.id !== oldData.id);
      console.log(`🗑️ [SmartListener-Supabase] Event supprimé: ${oldData.id}`);
    }

    // Trier par date
    updatedEvents.sort((a, b) => {
      const dateA = a.datetime instanceof Date ? a.datetime.getTime() : 0;
      const dateB = b.datetime instanceof Date ? b.datetime.getTime() : 0;
      return dateA - dateB;
    });

    // Mettre à jour le cache local et notifier
    this.lastOrganizerData.set(organizerId, updatedEvents);
    this.notifyOrganizerConsumers(organizerId, updatedEvents);
  }

  /**
   * 📦 Charger les événements organisateur initiaux
   */
  private async loadInitialOrganizerEvents(organizerId: string) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('organizer_id', organizerId)
        .order('datetime', { ascending: true });

      if (error) throw error;

      const events: Event[] = (data ?? []).map(row => normalizeSupabaseEvent(row));

      console.log(`📦 [SmartListener-Supabase] ${events.length} events chargés pour organisateur ${organizerId}`);

      // Sauvegarder dans le cache
      for (const event of events) {
        await locationCache.saveOrganizerEvent(event);
      }

      // Stocker les dernières données
      this.lastOrganizerData.set(organizerId, events);

      // Notifier les consumers (forceNotify=true pour le chargement initial, sinon 0 events ne notifie jamais)
      this.notifyOrganizerConsumers(organizerId, events, true);
    } catch (error) {
      console.error(`❌ [SmartListener-Supabase] Erreur chargement organisateur ${organizerId}:`, error);
      // 🔧 FIX: Notify consumers even on error to stop loading state
      const lastData = this.lastOrganizerData.get(organizerId) || [];
      if (!this.lastOrganizerData.has(organizerId)) {
        this.lastOrganizerData.set(organizerId, []);
      }
      this.notifyOrganizerConsumers(organizerId, lastData);
    }
  }

  /**
   * 🔄 CHANNEL SÉRIES PRIVÉ
   */
  private seriesPollingIntervals = new Map<string, NodeJS.Timeout>();

  private async startSeriesChannel(organizerId: string, retryCount: number = 0) {
    console.log(`🔄 [SmartListener-Supabase] Démarrage channel séries ${organizerId} (tentative ${retryCount + 1})`);

    // Charger les données initiales
    await this.loadInitialSeries(organizerId);

    // Créer le channel Realtime
    const channelName = `series-${organizerId}-${Date.now()}`;
    // 🔧 FIX PRD Fiabilité découverte (item 3) : évite un 2e retry en double pour ce même channel
    let isSuperseded = false;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_series',
          filter: `organizer_id=eq.${organizerId}`
        },
        async (payload: RealtimePostgresChangesPayload<DbSeriesRow>) => {
          console.log(`📡 [SmartListener-Supabase] Changement détecté séries ${organizerId}:`, payload.eventType);
          await this.loadInitialSeries(organizerId);
        }
      )
      .subscribe((status, err) => {
        console.log(`📊 [SmartListener-Supabase] Status channel séries: ${status}`, err ? err : '');

        if (status === 'SUBSCRIBED') {
          console.log(`✅ [SmartListener-Supabase] Channel séries ${organizerId} actif via Realtime`);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          if (isSuperseded) return;

          // 🔧 FIX PRD Fiabilité découverte (item 3) : ne PAS reconnecter si ce channel a été
          // fermé volontairement (plus aucun consumer — pause TTL, destruction background).
          const consumers = this.seriesConsumers.get(organizerId);
          if (!consumers || consumers.size === 0) {
            console.log(`ℹ️ [SmartListener-Supabase] Channel séries ${organizerId} fermé (${status}) sans consumer actif, pas de reconnexion`);
            return;
          }

          isSuperseded = true;
          console.error(`❌ [SmartListener-Supabase] Channel séries ${organizerId} en erreur (${status}):`, err);

          if (retryCount < 2) {
            const delay = Math.pow(2, retryCount) * 1000;
            console.log(`🔄 [SmartListener-Supabase] Retry séries dans ${delay}ms...`);
            setTimeout(() => {
              supabase.removeChannel(channel);
              this.startSeriesChannel(organizerId, retryCount + 1);
            }, delay);
          } else {
            console.warn(`⚠️ [SmartListener-Supabase] Realtime séries indisponible - activation du polling (5s)`);
            this.pendingSeriesChannels.delete(organizerId); // 🔧 FIX RACE CONDITION (fallback polling)
            this.startSeriesPolling(organizerId);
          }
        }
      });

    this.activeSeriesChannels.set(organizerId, { channel });
    this.pendingSeriesChannels.delete(organizerId); // 🔧 FIX RACE CONDITION
  }

  private startSeriesPolling(organizerId: string) {
    if (this.seriesPollingIntervals.has(organizerId)) return;

    console.log(`⏰ [SmartListener-Supabase] Démarrage polling séries ${organizerId} (5s)`);

    const pollFn = async () => {
      const consumers = this.seriesConsumers.get(organizerId);
      if (!consumers || consumers.size === 0) {
        this.stopSeriesPolling(organizerId);
        return;
      }
      await this.loadInitialSeries(organizerId);
    };

    pollFn();
    const interval = setInterval(pollFn, 5000);
    this.seriesPollingIntervals.set(organizerId, interval);
  }

  private stopSeriesPolling(organizerId: string) {
    const interval = this.seriesPollingIntervals.get(organizerId);
    if (interval) {
      clearInterval(interval);
      this.seriesPollingIntervals.delete(organizerId);
    }
  }

  /**
   * 📦 Charger les séries initiales
   */
  private async loadInitialSeries(organizerId: string) {
    try {
      const { data, error } = await supabase
        .from('event_series')
        .select('*')
        .eq('organizer_id', organizerId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const series: EventSeries[] = (data ?? []).map(row => normalizeSupabaseSeries(row));

      console.log(`📦 [SmartListener-Supabase] ${series.length} séries chargées pour organisateur ${organizerId}`);

      // Sauvegarder dans IndexedDB
      for (const seriesData of series) {
        await locationCache.saveSeries(seriesData);
      }

      // Stocker les dernières données
      this.lastSeriesData.set(organizerId, series);

      // Notifier les consumers
      this.notifySeriesConsumers(organizerId, series);
    } catch (error) {
      console.error(`❌ [SmartListener-Supabase] Erreur chargement séries ${organizerId}:`, error);
    }
  }

  /**
   * 📢 NOTIFICATION CONSUMERS - Avec vérification de changement
   */
  private notifyGeoConsumers(zoneKey: string, events: Event[], forceNotify = false) {
    const consumers = this.geoConsumers.get(zoneKey);
    if (!consumers || consumers.size === 0) return;

    // 🔧 FIX: Vérifier si les données ont vraiment changé
    const prevData = this.lastNotifiedGeoData.get(zoneKey) || [];
    if (!forceNotify && !hasEventsChanged(prevData, events)) {
      // Pas de changement réel, skip la notification
      return;
    }

    console.log(`🔄 [SmartListener-Supabase] Update géo zone ${zoneKey}: ${events.length} événements`);
    this.lastNotifiedGeoData.set(zoneKey, events);
    consumers.forEach(consumer => consumer.callback(events));
    console.log(`📢 [SmartListener-Supabase] Notification ${consumers.size} consumers pour zone ${zoneKey}`);
  }

  private notifyOrganizerConsumers(organizerId: string, events: Event[], forceNotify = false) {
    const consumers = this.organizerConsumers.get(organizerId);
    if (!consumers || consumers.size === 0) return;

    // 🔧 FIX: Vérifier si les données ont vraiment changé
    const prevData = this.lastNotifiedOrganizerData.get(organizerId) || [];
    if (!forceNotify && !hasEventsChanged(prevData, events)) {
      // Pas de changement réel, skip la notification
      return;
    }

    console.log(`🔄 [SmartListener-Supabase] Update organisateur ${organizerId}: ${events.length} événements`);
    this.lastNotifiedOrganizerData.set(organizerId, events);
    consumers.forEach(consumer => consumer.callback(events));
    console.log(`📢 [SmartListener-Supabase] Notification ${consumers.size} consumers pour organisateur ${organizerId}`);
  }

  private notifySeriesConsumers(organizerId: string, series: EventSeries[], forceNotify = false) {
    const consumers = this.seriesConsumers.get(organizerId);
    if (!consumers || consumers.size === 0) return;

    // 🔧 FIX: Vérifier si les données ont vraiment changé
    const prevData = this.lastNotifiedSeriesData.get(organizerId) || [];
    if (!forceNotify && !hasSeriesChanged(prevData, series)) {
      // Pas de changement réel, skip la notification
      return;
    }

    console.log(`🔄 [SmartListener-Supabase] Update séries ${organizerId}: ${series.length} séries`);
    this.lastNotifiedSeriesData.set(organizerId, series);
    consumers.forEach(consumer => consumer.callback(series));
    console.log(`📢 [SmartListener-Supabase] Notification ${consumers.size} consumers pour séries ${organizerId}`);
  }

  /**
   * 🧹 DÉSABONNEMENTS
   */
  private unsubscribeFromGeoEvents(zoneKey: string, consumerId: string) {
    const consumers = this.geoConsumers.get(zoneKey);
    if (consumers) {
      consumers.forEach(consumer => {
        if (consumer.id === consumerId) {
          consumers.delete(consumer);
        }
      });

      console.log(`🔇 [SmartListener-Supabase] Consumer ${consumerId} désabonné de zone ${zoneKey}. Restants: ${consumers.size}`);

      if (consumers.size === 0) {
        const channelInfo = this.activeGeoChannels.get(zoneKey);
        if (channelInfo) {
          console.log(`⏸️ [SmartListener-Supabase] Mise en pause channel pour zone ${zoneKey}`);

          this.pausedGeoChannels.set(zoneKey, {
            ...channelInfo,
            pausedAt: Date.now()
          });

          this.activeGeoChannels.delete(zoneKey);
          this.geoConsumers.delete(zoneKey);

          // Programmer la destruction après TTL
          setTimeout(() => {
            const pausedChannel = this.pausedGeoChannels.get(zoneKey);
            if (pausedChannel) {
              console.log(`🛑 [SmartListener-Supabase] Destruction channel zone ${zoneKey}`);
              supabase.removeChannel(pausedChannel.channel);
              this.pausedGeoChannels.delete(zoneKey);
              this.lastGeoData.delete(zoneKey);
              this.lastNotifiedGeoData.delete(zoneKey);
              this.geoChannelParams.delete(zoneKey);
            }
          }, SmartListenerService.PAUSED_LISTENER_TTL);
        }
      }
    }
  }

  private unsubscribeFromOrganizerEvents(organizerId: string, consumerId: string) {
    const consumers = this.organizerConsumers.get(organizerId);
    if (consumers) {
      consumers.forEach(consumer => {
        if (consumer.id === consumerId) {
          consumers.delete(consumer);
        }
      });

      console.log(`🔇 [SmartListener-Supabase] Consumer ${consumerId} désabonné d'organizer ${organizerId}. Restants: ${consumers.size}`);

      if (consumers.size === 0) {
        const channelInfo = this.activeOrganizerChannels.get(organizerId);
        if (channelInfo) {
          console.log(`⏸️ [SmartListener-Supabase] Mise en pause channel organisateur ${organizerId}`);

          this.pausedOrganizerChannels.set(organizerId, {
            ...channelInfo,
            pausedAt: Date.now()
          });

          this.activeOrganizerChannels.delete(organizerId);
          this.organizerConsumers.delete(organizerId);

          setTimeout(() => {
            const pausedChannel = this.pausedOrganizerChannels.get(organizerId);
            if (pausedChannel) {
              console.log(`🛑 [SmartListener-Supabase] Destruction channel organisateur ${organizerId}`);
              supabase.removeChannel(pausedChannel.channel);
              this.pausedOrganizerChannels.delete(organizerId);
              this.lastOrganizerData.delete(organizerId);
              this.lastNotifiedOrganizerData.delete(organizerId);
            }
          }, SmartListenerService.PAUSED_LISTENER_TTL);
        }
      }
    }
  }

  private unsubscribeFromSeries(organizerId: string, consumerId: string) {
    const consumers = this.seriesConsumers.get(organizerId);
    if (consumers) {
      consumers.forEach(consumer => {
        if (consumer.id === consumerId) {
          consumers.delete(consumer);
        }
      });

      console.log(`🔇 [SmartListener-Supabase] Consumer ${consumerId} désabonné des séries ${organizerId}. Restants: ${consumers.size}`);

      if (consumers.size === 0) {
        const channelInfo = this.activeSeriesChannels.get(organizerId);
        if (channelInfo) {
          console.log(`⏸️ [SmartListener-Supabase] Mise en pause channel séries ${organizerId}`);

          this.pausedSeriesChannels.set(organizerId, {
            ...channelInfo,
            pausedAt: Date.now()
          });

          this.activeSeriesChannels.delete(organizerId);
          this.seriesConsumers.delete(organizerId);

          setTimeout(() => {
            const pausedChannel = this.pausedSeriesChannels.get(organizerId);
            if (pausedChannel) {
              console.log(`🛑 [SmartListener-Supabase] Destruction channel séries ${organizerId}`);
              supabase.removeChannel(pausedChannel.channel);
              this.pausedSeriesChannels.delete(organizerId);
              this.lastSeriesData.delete(organizerId);
              this.lastNotifiedSeriesData.delete(organizerId);
            }
          }, SmartListenerService.PAUSED_LISTENER_TTL);
        }
      }
    }
  }

  /**
   * 🔄 FORCE REFRESH - Recharger les données explicitement
   * Utilisé après une modification d'événement pour garantir la cohérence
   */
  async forceRefreshOrganizerEvents(organizerId: string): Promise<void> {
    console.log(`🔄 [SmartListener-Supabase] Force refresh pour organisateur ${organizerId}`);

    // Vérifier si un channel existe (actif ou en pause)
    const hasActiveChannel = this.activeOrganizerChannels.has(organizerId);
    const hasPausedChannel = this.pausedOrganizerChannels.has(organizerId);
    const hasConsumers = this.organizerConsumers.has(organizerId) && this.organizerConsumers.get(organizerId)!.size > 0;

    if (hasActiveChannel || hasPausedChannel || hasConsumers) {
      // Recharger les données et notifier les consumers
      await this.loadInitialOrganizerEvents(organizerId);
      console.log(`✅ [SmartListener-Supabase] Force refresh terminé pour ${organizerId}`);
    } else {
      console.log(`⚠️ [SmartListener-Supabase] Pas de channel/consumers pour ${organizerId}, refresh ignoré`);
    }
  }

  /**
   * 🔄 FORCE REFRESH SERIES - Recharger les séries explicitement
   */
  async forceRefreshSeries(organizerId: string): Promise<void> {
    console.log(`🔄 [SmartListener-Supabase] Force refresh séries pour ${organizerId}`);

    const hasActiveChannel = this.activeSeriesChannels.has(organizerId);
    const hasPausedChannel = this.pausedSeriesChannels.has(organizerId);
    const hasConsumers = this.seriesConsumers.has(organizerId) && this.seriesConsumers.get(organizerId)!.size > 0;

    if (hasActiveChannel || hasPausedChannel || hasConsumers) {
      await this.loadInitialSeries(organizerId);
      console.log(`✅ [SmartListener-Supabase] Force refresh séries terminé pour ${organizerId}`);
    } else {
      console.log(`⚠️ [SmartListener-Supabase] Pas de channel/consumers séries pour ${organizerId}, refresh ignoré`);
    }
  }

  /**
   * 📊 STATS ET DEBUG
   */
  getStats() {
    const stats = {
      geoChannels: this.activeGeoChannels.size,
      organizerChannels: this.activeOrganizerChannels.size,
      seriesChannels: this.activeSeriesChannels.size,
      pausedGeoChannels: this.pausedGeoChannels.size,
      pausedOrganizerChannels: this.pausedOrganizerChannels.size,
      pausedSeriesChannels: this.pausedSeriesChannels.size,
      totalChannels: this.activeGeoChannels.size + this.activeOrganizerChannels.size + this.activeSeriesChannels.size,
      totalWithPaused: this.activeGeoChannels.size + this.activeOrganizerChannels.size + this.activeSeriesChannels.size +
        this.pausedGeoChannels.size + this.pausedOrganizerChannels.size + this.pausedSeriesChannels.size,
      geoConsumers: Array.from(this.geoConsumers.entries()).map(([zone, consumers]) => ({
        zone,
        consumers: consumers.size
      })),
      organizerConsumers: Array.from(this.organizerConsumers.entries()).map(([org, consumers]) => ({
        organizer: org,
        consumers: consumers.size
      })),
      seriesConsumers: Array.from(this.seriesConsumers.entries()).map(([org, consumers]) => ({
        organizer: org,
        consumers: consumers.size
      })),
    };

    console.log('📊 [SmartListener-Supabase] getStats():', stats);
    return stats;
  }

  /**
   * 🧹 NETTOYAGE COMPLET
   */
  clearAll(): void {
    console.log('🧹 [SmartListener-Supabase] Nettoyage complet...');

    // Arrêter tous les channels actifs
    this.activeGeoChannels.forEach((info, zoneKey) => {
      console.log(`🛑 [SmartListener-Supabase] Arrêt channel géo: ${zoneKey}`);
      supabase.removeChannel(info.channel);
    });

    this.activeOrganizerChannels.forEach((info, organizerId) => {
      console.log(`🛑 [SmartListener-Supabase] Arrêt channel organizer: ${organizerId}`);
      supabase.removeChannel(info.channel);
    });

    this.activeSeriesChannels.forEach((info, organizerId) => {
      console.log(`🛑 [SmartListener-Supabase] Arrêt channel séries: ${organizerId}`);
      supabase.removeChannel(info.channel);
    });

    // Arrêter les channels en pause
    this.pausedGeoChannels.forEach((info, zoneKey) => {
      console.log(`🛑 [SmartListener-Supabase] Arrêt channel géo en pause: ${zoneKey}`);
      supabase.removeChannel(info.channel);
    });

    this.pausedOrganizerChannels.forEach((info, organizerId) => {
      console.log(`🛑 [SmartListener-Supabase] Arrêt channel org en pause: ${organizerId}`);
      supabase.removeChannel(info.channel);
    });

    this.pausedSeriesChannels.forEach((info, organizerId) => {
      console.log(`🛑 [SmartListener-Supabase] Arrêt channel séries en pause: ${organizerId}`);
      supabase.removeChannel(info.channel);
    });

    // Nettoyer toutes les maps
    this.activeGeoChannels.clear();
    this.activeOrganizerChannels.clear();
    this.activeSeriesChannels.clear();
    this.pausedGeoChannels.clear();
    this.pausedOrganizerChannels.clear();
    this.pausedSeriesChannels.clear();
    this.pendingGeoChannels.clear();
    this.pendingOrganizerChannels.clear();
    this.pendingSeriesChannels.clear();
    this.geoConsumers.clear();
    this.organizerConsumers.clear();
    this.seriesConsumers.clear();
    this.lastGeoData.clear();
    this.lastOrganizerData.clear();
    this.lastSeriesData.clear();
    this.geoChannelParams.clear();

    console.log('✅ [SmartListener-Supabase] Nettoyage complet terminé');
  }

  /**
   * 👁️ DÉTECTION DE VISIBILITÉ PWA
   */
  private setupVisibilityDetection() {
    if (typeof document === 'undefined') return;

    console.log('👁️ [SmartListener-Supabase] Configuration détection visibilité PWA');

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('🌙 [SmartListener-Supabase] App en arrière-plan, programmation pause (5min)');
        this.isAppVisible = false;
        this.scheduleBackgroundPause();
      } else {
        console.log('☀️ [SmartListener-Supabase] App redevient visible, annulation pause');
        this.isAppVisible = true;
        this.cancelBackgroundPause();
        this.resumeAllPausedChannels();
        this.setupInteractionWakeup();
      }
    });

    this.isAppVisible = !document.hidden;
    if (this.isAppVisible) {
      this.setupInteractionWakeup();
    }
  }

  private scheduleBackgroundPause() {
    if (this.visibilityTimeoutId) {
      clearTimeout(this.visibilityTimeoutId);
    }

    this.visibilityTimeoutId = setTimeout(() => {
      if (!this.isAppVisible && (this.activeGeoChannels.size > 0 || this.activeOrganizerChannels.size > 0)) {
        console.log('⏸️ [SmartListener-Supabase] 5min arrière-plan dépassées, mise en pause channels');
        this.pauseAllActiveChannels();
      }
    }, SmartListenerService.BACKGROUND_PAUSE_DELAY) as any;
  }

  private cancelBackgroundPause() {
    if (this.visibilityTimeoutId) {
      clearTimeout(this.visibilityTimeoutId);
      this.visibilityTimeoutId = null;
    }
  }

  private pauseAllActiveChannels() {
    this.activeGeoChannels.forEach((info, zoneKey) => {
      console.log(`⏸️ [SmartListener-Supabase] Pause automatique channel géo zone ${zoneKey}`);

      this.pausedGeoChannels.set(zoneKey, {
        ...info,
        pausedAt: Date.now()
      });

      this.activeGeoChannels.delete(zoneKey);

      setTimeout(() => {
        const pausedChannel = this.pausedGeoChannels.get(zoneKey);
        if (pausedChannel) {
          console.log(`🛑 [SmartListener-Supabase] Destruction channel géo ${zoneKey} après 10 jours background`);
          supabase.removeChannel(pausedChannel.channel);
          this.pausedGeoChannels.delete(zoneKey);
        }
      }, SmartListenerService.BACKGROUND_PAUSED_TTL);
    });
  }

  private resumeAllPausedChannels() {
    if (this.pausedGeoChannels.size === 0) return;

    console.log(`🔄 [SmartListener-Geohash] Réveil de ${this.pausedGeoChannels.size} channels en pause`);

    this.pausedGeoChannels.forEach((info, zoneKey) => {
      this.activeGeoChannels.set(zoneKey, info);
      console.log(`☀️ [SmartListener-Geohash] Channel geo zone ${zoneKey} reveille`);

      // Recharger les donnees FRAICHES depuis Supabase
      const params = this.geoChannelParams.get(zoneKey);
      if (params && params.geohashes) {
        // Utiliser la nouvelle methode geohash
        const zoneInfo: GeohashZoneInfo = {
          centerHash: zoneKey.replace(/^geo_/, '').replace(/_\d+$/, ''),
          allHashes: params.geohashes,
          center: params.center,
          radius: params.radius
        };
        console.log(`🔄 [SmartListener-Geohash] Rechargement geohash pour zone ${zoneInfo.centerHash}`);
        this.loadInitialGeoEventsByGeohash(zoneKey, zoneInfo);
      } else if (params) {
        // Fallback ancienne methode
        console.log(`🔄 [SmartListener-Supabase] Rechargement legacy pour zone ${zoneKey}`);
        this.loadInitialGeoEvents(zoneKey, params.center, params.radius);
      }
    });

    this.pausedGeoChannels.clear();
  }

  private setupInteractionWakeup() {
    if (this.interactionListenersSetup) return;

    console.log('👆 [SmartListener-Supabase] Configuration réveil sur interaction');

    const wakeupEvents = ['click', 'scroll', 'keydown', 'touchstart'];
    const wakeupHandler = () => {
      if (this.pausedGeoChannels.size > 0) {
        console.log('👆 [SmartListener-Supabase] Interaction détectée, réveil channels');
        this.resumeAllPausedChannels();
      }
    };

    wakeupEvents.forEach(eventType => {
      document.addEventListener(eventType, wakeupHandler, {
        passive: true,
        capture: false
      });
    });

    this.interactionListenersSetup = true;
  }
}

export const smartListenerService = SmartListenerService.getInstance();

// 🔧 DEBUG: Exposer le service dans la console pour le debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__smartListener = smartListenerService;
  console.log('🔧 DEBUG: SmartListenerService accessible via window.__smartListener');
}

/**
 * 🔍 FIX PRD Fiabilité découverte (item 4) — Résultat détaillé permettant à l'UI
 * (EventModalRoute) de distinguer "vraiment introuvable" (event supprimé/dépublié,
 * ou ID invalide) d'une "erreur réseau" (retry possible), au lieu de recevoir `null`
 * dans les deux cas.
 */
export type GetEventByIdResult =
  | { status: 'found'; event: Event }
  | { status: 'not_found' }
  | { status: 'network_error' };

/**
 * 🔍 FONCTION HELPER (détaillée) - Récupérer un événement par ID.
 * 🔧 FIX PRD Fiabilité découverte (item 1) : la lecture cache IndexedDB et l'écriture
 * cache sont chacune isolées dans leur propre try/catch, séparément de l'appel réseau.
 * Le cache est une optimisation : un échec (navigation privée Safari, quota dépassé...)
 * ne doit jamais être confondu avec "introuvable" ni avec une erreur réseau.
 */
export const getEventByIdDetailed = async (eventId: string): Promise<GetEventByIdResult> => {
  if (!eventId || eventId.trim() === '' || eventId === 'undefined' || eventId === 'null') {
    console.warn('❌ ID d\'événement invalide:', eventId);
    return { status: 'not_found' };
  }

  console.log('🔍 [getEventById-Supabase] Recherche événement:', eventId);

  // 1) Cache IndexedDB (best-effort) — un échec de lecture ne doit jamais empêcher le fallback réseau
  try {
    const geoEvents = await locationCache.getGeoEvents('all', 50);
    const geoEvent = geoEvents.find(event => event.id === eventId);
    if (geoEvent) {
      console.log('💾 [getEventById-Supabase] Trouvé dans geoEvents:', eventId);
      return { status: 'found', event: geoEvent };
    }
  } catch (cacheError) {
    console.warn('⚠️ [getEventById-Supabase] Lecture cache échouée, on continue via réseau:', cacheError);
  }

  // 2) Réseau = source de vérité
  let data: DbEventRow | null;
  try {
    console.log('🔥 [getEventById-Supabase] Fallback Supabase pour:', eventId);
    const { data: rowData, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .single();

    console.log('📥 [getEventById-Supabase] Réponse Supabase:', {
      hasData: !!rowData,
      error: error?.message,
      errorCode: error?.code
    });

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('❌ [getEventById-Supabase] Événement non trouvé:', eventId);
        return { status: 'not_found' };
      }
      console.error('❌ [getEventById-Supabase] Erreur Supabase:', error);
      throw error;
    }

    data = rowData;
  } catch (networkError) {
    console.error('❌ [getEventById-Supabase] Erreur réseau:', networkError);
    return { status: 'network_error' };
  }

  if (!data) return { status: 'not_found' };

  const event = normalizeSupabaseEvent(data);
  console.log('✅ [getEventById-Supabase] Événement trouvé via Supabase:', eventId);

  // 3) Écriture cache (best-effort) — un échec ici ne doit jamais transformer
  // un succès réseau en "introuvable"
  if (event.organizerId) {
    await safeCacheWrite(() => locationCache.saveOrganizerEvent(event), `saveOrganizerEvent(${event.id})`);
  } else {
    await safeCacheWrite(() => locationCache.saveGeoEvent(event, 'global'), `saveGeoEvent(${event.id})`);
  }

  return { status: 'found', event };
};

/**
 * 🔍 FONCTION HELPER (rétrocompatible) - Récupérer un événement par ID.
 * Conserve la signature historique `Promise<Event | null>` pour les appelants existants
 * (useModalStack, etc.) qui n'ont pas besoin de distinguer "introuvable" de "erreur réseau".
 * Pour cette distinction (utilisée par EventModalRoute), voir `getEventByIdDetailed`.
 */
export const getEventById = async (eventId: string): Promise<Event | null> => {
  const result = await getEventByIdDetailed(eventId);
  return result.status === 'found' ? result.event : null;
};

// ✅ MIGRATION SUPABASE: normalizeFirestoreEvent retiré - utiliser normalizeSupabaseEvent
